import { useState, useEffect, useCallback } from 'react';
import { searchUsageSessions } from '../api/api';
import styles from './ErrorSessionDetailModal.module.scss';

const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function formatDuration(minutes) {
  if (minutes === null || minutes === undefined || minutes === 0) {
    return '0 min';
  }
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatTime(isoString) {
  if (!isoString) {
    return '';
  }
  const date = new Date(isoString);
  return date.toLocaleString('default', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: browserTimeZone,
    timeZoneName: 'short',
  });
}

const EMPTY_SCOPE = { machineIds: [] };

// `scope.machineIds` mirrors the page-level fleet scope; [] means every machine.
function UsageSessionDetailModal({ machines = [], scope = null, initialDate, onClose }) {
  const { machineIds } = scope || EMPTY_SCOPE;
  const getDefaultDateRange = useCallback(() => {
    if (initialDate) {
      return {
        startDate: initialDate,
        endDate: initialDate,
      };
    }
    const now = new Date();
    const monthsToShow = 3;
    const start = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (monthsToShow - 1), 1)
    );
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
    };
  }, [initialDate]);

  const defaultRange = getDefaultDateRange();

  const [filterMachineId, setFilterMachineId] = useState('');
  const [filterStartDate, setFilterStartDate] = useState(defaultRange.startDate);
  const [filterEndDate, setFilterEndDate] = useState(defaultRange.endDate);
  const [appliedFilters, setAppliedFilters] = useState({
    machineId: '',
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
  });
  const [sessions, setSessions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total_count: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchSessions = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          startTime: `${appliedFilters.startDate}T00:00:00Z`,
          endTime: `${appliedFilters.endDate}T23:59:59Z`,
          page,
          pageSize: 20,
        };
        if (appliedFilters.machineId) {
          params.machineId = parseInt(appliedFilters.machineId);
        } else if (machineIds.length) {
          params.machineIds = machineIds;
        }
        const res = await searchUsageSessions(params);
        setSessions(res.data.sessions || []);
        setPagination(res.data.pagination || { page: 1, total_pages: 1, total_count: 0 });
      } catch (err) {
        console.error('Error searching usage sessions:', err);
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters, machineIds]
  );

  useEffect(() => {
    fetchSessions(currentPage);
  }, [currentPage, fetchSessions]);

  const handleSearch = () => {
    setAppliedFilters({
      machineId: filterMachineId,
      startDate: filterStartDate,
      endDate: filterEndDate,
    });
    setCurrentPage(1);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleBackdropClick = e => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.modal} onClick={handleBackdropClick}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Usage Session Details</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className={styles.filters}>
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label>Machine:</label>
              <select value={filterMachineId} onChange={e => setFilterMachineId(e.target.value)}>
                <option value="">All Machines</option>
                {machines.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.nickname || m.serial} ({m.serial})
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label>From:</label>
              <input
                type="date"
                value={filterStartDate}
                onChange={e => setFilterStartDate(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div className={styles.filterGroup}>
              <label>To:</label>
              <input
                type="date"
                value={filterEndDate}
                onChange={e => setFilterEndDate(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <button type="button" className={styles.searchButton} onClick={handleSearch}>
              Search
            </button>
          </div>
        </div>

        <div className={styles.resultInfo}>
          {pagination.total_count} session{pagination.total_count !== 1 ? 's' : ''} found
        </div>

        <div className={styles.tableContainer}>
          {loading ? (
            <div className={styles.loadingMessage}>Loading...</div>
          ) : sessions.length === 0 ? (
            <div className={styles.noData}>No sessions found</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Machine</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Duration</th>
                  <th>End Reason</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(session => {
                  const startTime = new Date(session.start_time);
                  const endTime = session.end_time ? new Date(session.end_time) : new Date();
                  const durationMinutes = Math.round((endTime - startTime) / (1000 * 60));
                  const isActive = !session.end_time;

                  return (
                    <tr key={session.id}>
                      <td>
                        <div className={styles.machineCell}>
                          <span className={styles.machineName}>
                            {session.nickname || session.serial}
                          </span>
                          {session.nickname && (
                            <span className={styles.machineSerial}>{session.serial}</span>
                          )}
                        </div>
                      </td>
                      <td>{formatTime(session.start_time)}</td>
                      <td>
                        {session.end_time ? (
                          formatTime(session.end_time)
                        ) : (
                          <span className={styles.active}>Active</span>
                        )}
                      </td>
                      <td>
                        <span className={isActive ? styles.active : ''}>
                          {formatDuration(durationMinutes)}
                          {isActive && ' (Active)'}
                        </span>
                      </td>
                      <td className={styles.reasonCell}>{session.end_reason || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {pagination.total_pages > 1 && (
          <div className={styles.pagination}>
            <button disabled={!pagination.has_prev} onClick={() => setCurrentPage(p => p - 1)}>
              Previous
            </button>
            <span className={styles.pageInfo}>
              {pagination.page} / {pagination.total_pages}
            </span>
            <button disabled={!pagination.has_next} onClick={() => setCurrentPage(p => p + 1)}>
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default UsageSessionDetailModal;
